import { Box, Grid } from "@mui/material";
import { useFormik } from "formik";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import AsyncSelect from "@/components/AsyncSelect";
import AsyncSelectScroll from "@/components/AsyncSelectScroll";
import OutlineCard from "@/components/OutlineCard";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import StyledModal from "@/components/globalModal/StyledModal";
import { useRoleController } from "@/components/useRoleController";
import { PROVIDER_PATHS } from "@/constants/paths";
import { useDebounce } from "@/hooks/use-debounce";
import { useVisitNoteFormOptions } from "@/hooks/use-visit-note-form-options";
import useDisclosure, { DisclosureHost } from "@/hooks/use-disclosure";
import { useAppointmentsV2 } from "@/queries/patient-portal/forms/appointment/use-appointment-list-v2";
import { INPUT_FIELD_HEIGHT } from "@/theme";
import { formatDateTime } from "@/utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "@/utils/utils";
import NewAppointment from "@/pages/app/provider/scheduling/appointment/NewAppointment";
interface CreateVisitNoteModalProps {
  disclosureHost: DisclosureHost;
}

const CreateVisitNoteModal = ({
  disclosureHost,
}: CreateVisitNoteModalProps) => {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const newAppointmentDisclosure = useDisclosure();
  const [searchKey, setSearchKey] = useDebounce<string>("");
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const { options: noteTypeOptions, defaultOption: defaultNoteType } =
    useVisitNoteFormOptions();
  const { hasUpdate: hasUpdateAppointment } = useRoleController(
    "Appointment Scheduling"
  );
  const {
    data: visitDetails,
    isFetching: isFetchingVisitDetails,
    isLoading: isLoadingVisitDetails,
  } = useAppointmentsV2({
    providerGroupId: getXtenantId(),
    pagination,
    patientUuid: patientId ? [patientId] : undefined,
    appointmentStatus: ["CONFIRMED", "PENDING"] as any, // note : PENDING is for scheduled appointments.
    searchKey,
  });

  const initialValue = {
    appointment: undefined,
    noteType: defaultNoteType,
  };

  const validationSchema = Yup.object({
    appointment: Yup.object().required().label("Appointment"),
    noteType: Yup.object().required().label("Note Type"),
  });
  const formik = useFormik({
    initialValues: initialValue,
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  async function handleSubmit(values?: any) {
    // note: rediraction to create new visit note
    if (values) {
      navigate(
        `${PROVIDER_PATHS.NEW_PATIENT}/${patientId}/visit-note/form?appointmentid=${values?.appointment?.value}&noteType=${values?.noteType?.value}`
      );
      handleClose();
    }
  }

  function handleClose() {
    formik.resetForm();
    disclosureHost.close();
  }

  return (
    <StyledModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle="Create Visit Note"
      dialogAction={
        <>
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={handleClose}
          />
          <StyledButtonV2
            label="Continue"
            onClick={formik.submitForm}
            variant="contained"
            sx={{ ml: 1.2 }}
          />
        </>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "32vw",
          height: "27vh",
        },
      }}
    >
      <NewAppointment
        opened={newAppointmentDisclosure.opened}
        onClose={newAppointmentDisclosure.close}
        hideRecurring={true}
        enablePrefillPatientData
      />
      <Grid container spacing={3} pt={2}>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={8}>
            <AsyncSelectScroll
              disablePortal={false}
              required={true}
              enableFilterOption
              handleBlur={formik.handleBlur}
              onSearch={(v) => {
                setSearchKey(v);
                setPagination((p) => ({ ...p, page: 0 }));
              }}
              pagination={pagination}
              setPagination={setPagination}
              totalElements={visitDetails?.data?.page?.totalElements}
              placeholder="Select Appointment"
              label="Select Appointment"
              // required
              name="appointment"
              loading={isLoadingVisitDetails || isFetchingVisitDetails}
              renderCustomLabel={(option) => {
                const time = option?.startTime
                  ? formatDateTime(option?.startTime)
                  : "-";
                const HoveredBox = (option: any) =>
                  `${time}  | ${replaceUnderScoreAddSpace(option.label)}`;

                return (
                  <Box width="100%">
                    <OutlineCard>
                      <Box>{HoveredBox(option)}</Box>
                      <Text>Reason for Visit: {option.chiefComplaint}</Text>
                    </OutlineCard>
                  </Box>
                );
              }}
              options={
                visitDetails?.data?.content?.map((option) => {
                  return {
                    value: option.uuid,
                    label: ` ${option.type ?? "-"}`,
                    startTime: option?.startTime,
                    chiefComplaint: option?.chiefComplaint,
                    uuid: option.uuid,
                  };
                }) ?? []
              }
              value={formik.values.appointment as any}
              onChange={(option: any) => {
                formik.setFieldValue("appointment", option);
              }}
              error={
                (formik.touched?.appointment &&
                  formik.errors?.appointment) as any
              }
            />
          </Grid>
          {hasUpdateAppointment && (
            <Grid item xs={4}>
              <StyledButtonV2
                label="Create New"
                sx={{
                  width: "100%",
                  height: INPUT_FIELD_HEIGHT,
                }}
                style={{
                  marginBottom:
                    formik.touched?.appointment &&
                    (formik.errors?.appointment as any)
                      ? "20px"
                      : "0px",
                }}
                variant="outlined"
                onClick={() => newAppointmentDisclosure.open()}
              />
            </Grid>
          )}
        </Grid>
        <Grid item xs={12}>
          <AsyncSelect
            required
            label="Select Note Type"
            placeholder="Select"
            value={(formik.values as any)?.noteType}
            name="noteType"
            disableSortOptions
            options={noteTypeOptions}
            onChange={(option: any) => formik.setFieldValue("noteType", option)}
            error={formik.touched?.noteType && (formik.errors?.noteType as any)}
          />
        </Grid>
      </Grid>
    </StyledModal>
  );
};

export default CreateVisitNoteModal;
